import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { getCurrentUser, isAdmin } from './authService';
import { sanitizeContentBlocks } from '../utils/contentBlocks';

// Collection name for projects
const PROJECTS_COLLECTION = 'projects';

// Optimize image before upload
const optimizeImage = (file, maxWidth = 1200, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(resolve, 'image/jpeg', quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

// Upload image to Firebase Storage
export const uploadImage = async (file, path) => {
  try {
    // Optimize image before upload
    const optimizedFile = await optimizeImage(file);
    
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, optimizedFile);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

// Add a new project (admin only)
export const addProject = async (projectData) => {
  try {
    // Check if user is authenticated and is admin
    const currentUser = getCurrentUser();
    if (!currentUser || !isAdmin(currentUser)) {
      throw new Error('Unauthorized: Only admin can add projects');
    }

    // Generate unique project ID for storage path
    const tempId = Date.now().toString();
    
    // Upload main image to Storage if provided
    let mainImageUrl = '';
    if (projectData.mainImage && projectData.mainImage instanceof File) {
      const mainImagePath = `projects/${tempId}/main-image.${projectData.mainImage.name.split('.').pop()}`;
      mainImageUrl = await uploadImage(projectData.mainImage, mainImagePath);
    }
    
    // Upload gallery images to Storage if provided
    let galleryImageUrls = [];
    if (projectData.images && Array.isArray(projectData.images)) {
      const uploadPromises = projectData.images.map(async (image, index) => {
        if (image instanceof File) {
          const imagePath = `projects/${tempId}/gallery-${index}.${image.name.split('.').pop()}`;
          return await uploadImage(image, imagePath);
        }
        return null;
      });
      
      const results = await Promise.all(uploadPromises);
      galleryImageUrls = results.filter(url => url !== null);
    }
    
    // Prepare project data for Firestore (with Storage URLs)
    const projectToSave = {
      name: projectData.name,
      type: projectData.type,
      description: projectData.description,
      location: projectData.location || '',
      year: projectData.year || '',
      team: projectData.team || '',
      client: projectData.client || '',
      awards: projectData.awards || '',
      spacing: projectData.spacing || '',
      contentBlocks: sanitizeContentBlocks(projectData.contentBlocks),
      status: projectData.status || 'Completed',
      isFeatured: !!projectData.isFeatured,
      // Store Firebase Storage URLs instead of base64
      mainImage: mainImageUrl,
      images: galleryImageUrls,
      // Add testimonial support - handle both form fields and object format
      testimonial: (projectData.testimonialText && projectData.testimonialAuthor) ? {
        text: projectData.testimonialText,
        author: projectData.testimonialAuthor
      } : (projectData.testimonial ? {
        text: projectData.testimonial.text || '',
        author: projectData.testimonial.author || ''
      } : null),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const docRef = await addDoc(collection(db, PROJECTS_COLLECTION), projectToSave);
    return { id: docRef.id, ...projectToSave };
  } catch (error) {
    console.error('Error adding project:', error);
    throw error;
  }
};

// Get all projects
export const getProjects = async (includeDrafts = false) => {
  try {
    const querySnapshot = await getDocs(collection(db, PROJECTS_COLLECTION));
    const projects = [];
    querySnapshot.forEach((doc) => {
      const project = { id: doc.id, ...doc.data() };
      projects.push(project);
    });
    // Filter out drafts unless explicitly requested
    return includeDrafts ? projects : projects.filter(project => project.status !== 'Draft');
  } catch (error) {
    console.error('Error getting projects:', error);
    throw error;
  }
};

// Get projects by type/category
export const getProjectsByType = async (type, includeDrafts = false) => {
  try {
    const projects = await getProjects(includeDrafts);
    return projects.filter(project => project.type === type);
  } catch (error) {
    console.error('Error getting projects by type:', error);
    throw error;
  }
};

// Get single project by ID
export const getProjectById = async (projectId) => {
  try {
    const projects = await getProjects();
    return projects.find(project => project.id === projectId);
  } catch (error) {
    console.error('Error getting project by ID:', error);
    throw error;
  }
};

// Update project (admin only)
export const updateProject = async (projectId, updateData) => {
  try {
    // Check if user is authenticated and is admin
    const currentUser = getCurrentUser();
    if (!currentUser || !isAdmin(currentUser)) {
      throw new Error('Unauthorized: Only admin can update projects');
    }

    let updatePayload = { ...updateData };
    if (updateData.spacing !== undefined) {
      updatePayload.spacing = updateData.spacing;
    }

    if (updateData.contentBlocks !== undefined) {
      updatePayload.contentBlocks = sanitizeContentBlocks(updateData.contentBlocks);
    }

    if (updateData.isFeatured !== undefined) {
      updatePayload.isFeatured = !!updateData.isFeatured;
    }

    if (
      updateData.testimonialText !== undefined ||
      updateData.testimonialAuthor !== undefined ||
      updateData.testimonial !== undefined
    ) {
      updatePayload.testimonial = (updateData.testimonialText && updateData.testimonialAuthor) ? {
        text: updateData.testimonialText,
        author: updateData.testimonialAuthor
      } : (updateData.testimonial ? {
        text: updateData.testimonial.text || '',
        author: updateData.testimonial.author || ''
      } : null);

      delete updatePayload.testimonialText;
      delete updatePayload.testimonialAuthor;
    }

    // Handle mainImage: if it's a File, upload and use URL
    if (updateData.mainImage && updateData.mainImage instanceof File) {
      const mainImagePath = `projects/${projectId}/main-image.${updateData.mainImage.name.split('.').pop()}`;
      const mainImageUrl = await uploadImage(updateData.mainImage, mainImagePath);
      updatePayload.mainImage = mainImageUrl;
    }

    // Handle images array: if any are File, upload and replace with URLs
    if (updateData.images && Array.isArray(updateData.images)) {
      const uploadPromises = updateData.images.map(async (image, index) => {
        if (image instanceof File) {
          const imagePath = `projects/${projectId}/gallery-${index}.${image.name.split('.').pop()}`;
          return await uploadImage(image, imagePath);
        }
        return image; // already a URL
      });
      const results = await Promise.all(uploadPromises);
      updatePayload.images = results;
    }

    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
    await updateDoc(projectRef, {
      ...updatePayload,
      updatedAt: new Date()
    });
    return true;
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

// Delete project (admin only)
export const deleteProject = async (projectId) => {
  try {
    // Check if user is authenticated and is admin
    const currentUser = getCurrentUser();
    if (!currentUser || !isAdmin(currentUser)) {
      throw new Error('Unauthorized: Only admin can delete projects');
    }

    await deleteDoc(doc(db, PROJECTS_COLLECTION, projectId));
    return true;
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};